/**
 * The frame both figure screens are drawn in (REQ-021).
 *
 * The executive screen and the per-system screens differ in exactly one thing —
 * which readings they show — and in nothing else that matters: the same query,
 * the same sentence when the read fails, the same count of figures that could
 * not be measured, and the same tile beneath. So there is one component and two
 * configurations of it. Two screens would have been the cheaper change today
 * and the expensive one at the first correction made to only one of them, which
 * is the failure this repository has been burned by before.
 *
 * `select` is the whole difference, and it is the reason AC-003 survives the
 * refactor: this component never chooses a figure. The cap on the executive
 * screen lives where it always did — in the metric registry, which refuses an
 * eleventh `executive` position at module load, and in `executiveOrder`, which
 * shows the ones that claim one. Nothing that can be changed here enlarges it.
 */
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";
import MetricFigure from "./MetricFigure";
import type { MetricReading } from "./metrics";

export interface MetricScreenProps {
  title: string;
  icon: LucideIcon;
  /** What this screen is for, in the words of the requirement behind it. */
  intro: ReactNode;
  /** Which readings this screen shows, and in what order. */
  select: (readings: MetricReading[]) => MetricReading[];
  /** What an empty selection means here — never just "no data". */
  emptyMessage: string;
  /** Drawn between the header and the figures. */
  children?: ReactNode;
}

export default function MetricScreen({
  title,
  icon: Icon,
  intro,
  select,
  emptyMessage,
  children,
}: MetricScreenProps) {
  const fmt = useLocaleFormat();
  const metrics = useQuery({
    queryKey: ["dashboard", "metrics"],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { metrics: MetricReading[] };
        meta: { computedAt: string; unavailable: number };
      }>("/staff/dashboard/metrics");
      return response.data;
    },
    retry: false,
    refetchInterval: 5 * 60_000,
  });

  // Read through the error flag rather than around it. React-query keeps the
  // previous `data` when a refetch fails, and this screen refetches every five
  // minutes without retrying — so figures drawn from `data` alone would go on
  // showing the 10:00 numbers underneath a banner saying nothing below is
  // current. The banner's own words promise otherwise, and the promise is kept
  // here.
  const payload = metrics.isError ? undefined : metrics.data;
  const figures = select(payload?.data.metrics ?? []);
  const unmeasured = figures.filter(
    (figure) => figure.value.status !== "ok",
  ).length;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Icon className="h-5 w-5 text-sky-600" /> {title}
          </h1>
          <p className="mt-1 text-sm text-gray-600">{intro}</p>
        </div>
        {payload && (
          <p className="text-xs text-gray-500">
            Read {fmt.dateTime(payload.meta.computedAt)}
          </p>
        )}
      </div>

      {children}

      {metrics.isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {metrics.isError && (
        <div
          data-testid="metrics-error"
          className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            The dashboard could not be read. Nothing below is current, and no
            figure is shown from a previous read — a stale screen that looks
            live is worse than an empty one.
          </p>
        </div>
      )}

      {unmeasured > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            {unmeasured} of {figures.length} figures could not be measured. They
            are marked, not filled in.
          </p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {figures.map((figure) => (
          <MetricFigure key={figure.id} metric={figure} />
        ))}
      </div>

      {!metrics.isLoading && !metrics.isError && figures.length === 0 && (
        <p
          data-testid="metrics-empty"
          className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500"
        >
          {emptyMessage}
        </p>
      )}
    </div>
  );
}
